import { useEffect, useMemo, useState } from "react";
import {
  resolveTestSelection,
  type Project,
  type PublishedVersion,
  type Repository,
  type ResolvedTestSelection,
  type Revision,
  type TestSelection,
} from "@proof/core";
import { Pin } from "lucide-react";
import { Button, Control, Select } from "./components";

/** Retained draft revisions offered for testing (newest first, bounded). */
const retainedLimit = 50;

export interface TestSourceList {
  versions: PublishedVersion[];
  revisions: Revision[];
}

/** Published versions for everyone; retained draft revisions only when drafts are allowed. */
export function useTestSources(
  repository: Repository,
  project: Pick<Project, "id" | "tenantId">,
  allowDrafts: boolean,
) {
  const [sources, setSources] = useState<TestSourceList>(),
    [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    setSources(undefined);
    setError("");
    void Promise.all([
      repository.versions(project.tenantId, project.id),
      allowDrafts
        ? repository.history(project.tenantId, project.id).catch(() => [])
        : Promise.resolve([] as Revision[]),
    ])
      .then(([versions, revisions]) => {
        if (live) setSources({ versions, revisions });
      })
      .catch((cause: Error) => {
        if (live) setError(cause.message);
      });
    return () => {
      live = false;
    };
  }, [repository, project.id, project.tenantId, allowDrafts]);
  return { sources, error };
}

/** Stored project default, else latest published; demo authors start on their working draft. */
export function initialTestSelection(
  project: Project,
  sources: TestSourceList | undefined,
  allowDrafts: boolean,
  preferDraft = false,
): TestSelection {
  const stored = project.settings.testSelection;
  if (stored) return stored;
  if (preferDraft && allowDrafts) return { document: "current" };
  return sources?.versions.length || !allowDrafts
    ? { document: "published" }
    : { document: "current" };
}

export function useResolvedTestSelection(
  selection: TestSelection,
  current: Project,
  sources: TestSourceList | undefined,
  allowDrafts: boolean,
): ResolvedTestSelection | undefined {
  // Keyed by value: callers build the selection inline, so its identity changes every render.
  const key = selectionKey(selection);
  return useMemo(
    () =>
      sources &&
      resolveTestSelection(
        selection,
        { current, versions: sources.versions, revisions: sources.revisions },
        allowDrafts,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, current, sources, allowDrafts],
  );
}

export const selectionKey = (selection: TestSelection | undefined) =>
  selection ? `${selection.document}|${selection.form || ""}` : "none";

function sourceOptions(
  sources: TestSourceList,
  allowDrafts: boolean,
  currentRevision: number,
) {
  const versions = [...sources.versions].sort((a, b) => b.version - a.version);
  const revisions = allowDrafts
    ? [...sources.revisions]
        .filter((row) => row.revision !== currentRevision)
        .sort((a, b) => b.revision - a.revision)
        .slice(0, retainedLimit)
    : [];
  const when = (at: string) => {
    const date = new Date(at);
    return Number.isNaN(date.valueOf()) ? "" : ` · ${date.toLocaleString()}`;
  };
  return (
    <>
      {!!versions.length && (
        <optgroup label="Published">
          <option value="published">
            Latest published (v{versions[0].version})
          </option>
          {versions.map((row) => (
            <option key={row.id} value={`version:${row.id}`}>
              Published v{row.version}
            </option>
          ))}
        </optgroup>
      )}
      {allowDrafts && (
        <optgroup label="Drafts">
          <option value="current">Current draft</option>
          {revisions.map((row) => (
            <option key={row.id} value={`revision:${row.id}`}>
              Draft revision {row.revision}
              {row.reason ? ` · ${row.reason}` : ""}
              {when(row.at)}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

/**
 * Document version and "Form from" choices shared by Live proof, the test window
 * dialog and the impersonation windows.
 */
export function TestSelectionControls({
  sources,
  allowDrafts,
  value,
  onChange,
  current,
  resolved,
  storedDefault,
  onSaveDefault,
  idPrefix = "test",
}: {
  sources: TestSourceList;
  allowDrafts: boolean;
  value: TestSelection;
  onChange: (selection: TestSelection) => void;
  current: Project;
  resolved?: ResolvedTestSelection;
  storedDefault?: TestSelection;
  onSaveDefault?: (selection: TestSelection) => void | Promise<void>;
  idPrefix?: string;
}) {
  const [saving, setSaving] = useState(false);
  const options = sourceOptions(sources, allowDrafts, current.revision);
  const effective = resolved?.selection || value;
  const isDefault =
    selectionKey(storedDefault) === selectionKey(effective) ||
    (!storedDefault && selectionKey(effective) === "published|");
  const empty = !sources.versions.length && !allowDrafts;
  return (
    <div className="test-selection" data-testid={`${idPrefix}-selection`}>
      <div className="test-selection-row">
        <Control label="Document version">
          <Select
            value={empty ? "" : effective.document}
            disabled={empty}
            onChange={(event) =>
              onChange({
                document: event.target.value as TestSelection["document"],
                ...(value.form ? { form: value.form } : {}),
              })
            }
          >
            {empty && <option value="">No published versions</option>}
            {options}
          </Select>
        </Control>
        <Control label="Form from">
          <Select
            value={effective.form || ""}
            disabled={empty}
            onChange={(event) => {
              const form = event.target.value as TestSelection["document"];
              onChange(
                form && form !== effective.document
                  ? { document: effective.document, form }
                  : { document: effective.document },
              );
            }}
          >
            <option value="">Same as document</option>
            {options}
          </Select>
        </Control>
        {onSaveDefault && (
          <Button
            disabled={saving || isDefault || !resolved || !!resolved.fallback}
            title="Use this selection by default in Live proof, Test this form and impersonation for this project"
            onClick={async () => {
              setSaving(true);
              try {
                await onSaveDefault(effective);
              } finally {
                setSaving(false);
              }
            }}
          >
            <Pin size={15} />
            {isDefault ? "Project default" : "Make project default"}
          </Button>
        )}
      </div>
      {resolved && (
        <p className="test-selection-status" aria-live="polite">
          Testing <strong>{resolved.label}</strong>.{" "}
          {resolved.fallback ? `${resolved.fallback} ` : ""}
          {resolved.versionId
            ? "Generation and saved proofs use this immutable published version."
            : resolved.mirrored
              ? "The document and form come from different versions, so this is a preview only: publish to generate or save proofs."
              : "Drafts are a preview only: publish to generate or save proofs."}
        </p>
      )}
    </div>
  );
}
